import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BrandedImage } from "@/components/site/BrandedImage";
import { ShareButtons } from "@/components/site/ShareButtons";
import {
  Calendar, MapPin, Clock, Users, Ticket, ArrowLeft, CheckCircle, ChevronDown,
  Sparkles, CreditCard, Coins, Heart, Send, MessageCircle
} from "lucide-react";
import { waLink } from "@/lib/constants";

const TYPE_LABEL = { bootcamp: "Bootcamp", conferinta: "Conferință", online: "Online", support: "Suport" };
const STATUS_LABEL = {
  open: { l: "Disponibil", c: "text-[#3BA76D]", bg: "bg-[#3BA76D]/20 border-[#3BA76D]/40" },
  almost_full: { l: "Aproape plin", c: "text-[#D4A63D]", bg: "bg-[#D4A63D]/20 border-[#D4A63D]/40" },
  sold_out: { l: "Sold out", c: "text-[#C83A3A]", bg: "bg-[#C83A3A]/20 border-[#C83A3A]/40" },
  completed: { l: "Încheiat", c: "text-[#6C7A9C]", bg: "bg-white/5 border-white/10" },
};

const PARTICIPANT_TYPES = [
  { v: "tata", l: "Tată" },
  { v: "mama", l: "Mamă" },
  { v: "bunic", l: "Bunic / Bunică" },
  { v: "specialist", l: "Specialist (avocat, psiholog, etc.)" },
  { v: "altul", l: "Altul" },
];

const PAYMENT_OPTIONS = [
  { v: "full", l: "Plătesc integral", icon: CreditCard },
  { v: "deposit", l: "Plătesc avans (20%)", icon: Coins },
  { v: "sponsor", l: "Sponsorizez un loc pentru altcineva", icon: Heart },
];

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
};

export default function IntalnireDetail() {
  const { slug } = useParams();
  const [ev, setEv] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", city: "", country: "România",
    participant_type: "tata", story: "", special_needs: "", payment_option: "full",
    public: false, terms: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/events/${slug}`).then((r) => setEv(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center">
        <h1 className="font-display text-3xl text-[#F8F8F4]">Evenimentul nu a fost găsit</h1>
        <Link to="/intalniri" className="mt-6 inline-flex btn-gold rounded-sm px-6 py-3 text-[12px] font-bold tracking-widest">ÎNAPOI LA ÎNTÂLNIRI</Link>
      </div>
    );
  }
  if (!ev) return <div className="max-w-3xl mx-auto px-4 py-32 text-[#A0AABF]">Se încarcă...</div>;

  const status = STATUS_LABEL[ev.status] || STATUS_LABEL.open;
  const canBook = ev.status !== "sold_out" && ev.status !== "completed";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.terms) {
      toast.error("Trebuie să accepți termenii și condițiile");
      return;
    }
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error("Numele și email-ul sunt obligatorii");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/events/${slug}/book`, {
        event_id: ev.event_id,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || undefined,
        city: form.city.trim() || undefined,
        country: form.country || "România",
        participant_type: form.participant_type,
        story: form.story.trim() || undefined,
        special_needs: form.special_needs.trim() || undefined,
        payment_option: form.payment_option,
        public: form.public,
      });
      if (data?.checkout_url) {
        toast.success("Redirect spre plata Stripe...");
        setTimeout(() => { window.location.href = data.checkout_url; }, 800);
      } else {
        toast.success(data?.message || "Rezervare confirmată!");
        setEv((prev) => prev ? { ...prev, seats_remaining: Math.max(0, prev.seats_remaining - 1) } : prev);
        setForm({
          first_name: "", last_name: "", email: "", phone: "", city: "", country: "România",
          participant_type: "tata", story: "", special_needs: "", payment_option: "full", public: false, terms: false,
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la rezervare");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-intalnire-detail" className="text-[#F8F8F4]">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <BrandedImage src={ev.hero_image || ev.cover_image} alt={ev.title} className="absolute inset-0 w-full h-full" watermarkPosition="br" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1020]/60 via-[#0A1020]/85 to-[#0A1020] pointer-events-none z-[5]" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <Link to="/intalniri" data-testid="event-back" className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-widest text-[#F8F8F4]/80 hover:text-[#D4A63D] mb-6">
            <ArrowLeft className="w-3.5 h-3.5" /> ÎNAPOI LA ÎNTÂLNIRI
          </Link>
          <div className="flex gap-2 mb-4">
            <span className="text-[10px] font-bold tracking-widest uppercase text-[#D4A63D] bg-[#0A1020]/85 border border-[#D4A63D]/30 px-2.5 py-1 rounded-sm">
              {TYPE_LABEL[ev.event_type] || ev.event_type}
            </span>
            <span className={`text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-sm border ${status.bg} ${status.c}`}>{status.l}</span>
          </div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05]">{ev.title}</h1>
          {ev.description_short && (
            <p className="mt-4 text-lg md:text-xl text-[#A0AABF] leading-relaxed max-w-3xl">{ev.description_short}</p>
          )}

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="event-info-grid">
            <div className="cinematic-card rounded-sm p-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#A0AABF] mb-1"><Calendar className="w-3 h-3" /> Dată</div>
              <div className="text-[13px] text-[#F8F8F4]">{formatDate(ev.start_date)}</div>
            </div>
            <div className="cinematic-card rounded-sm p-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#A0AABF] mb-1"><MapPin className="w-3 h-3" /> Locație</div>
              <div className="text-[13px] text-[#F8F8F4]">{ev.location_city || "Online"}</div>
            </div>
            <div className="cinematic-card rounded-sm p-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#A0AABF] mb-1"><Users className="w-3 h-3" /> Locuri</div>
              <div className="text-[13px] text-[#F8F8F4]"><strong className="gold-text">{ev.seats_remaining}</strong> / {ev.seats_total} disponibile</div>
            </div>
            <div className="cinematic-card rounded-sm p-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#A0AABF] mb-1"><Ticket className="w-3 h-3" /> Preț</div>
              <div className="text-[13px] text-[#F8F8F4]">
                {ev.price > 0 ? <><strong className="gold-text">{ev.price.toFixed(0)}</strong> RON</> : <span className="text-[#3BA76D] font-bold">Gratuit</span>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT + BOOKING */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-8">
        {/* LEFT: Description + Agenda */}
        <div className="lg:col-span-2 space-y-8">
          {ev.trainer && (
            <div className="dense-card rounded-lg p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#D4A63D] mb-1">Trainer / Speaker</div>
              <div className="font-display text-xl text-[#F8F8F4]">{ev.trainer}</div>
            </div>
          )}

          {ev.description_long && (
            <div className="dense-card rounded-lg p-5" data-testid="event-description">
              <div className="text-[10px] uppercase tracking-widest text-[#D4A63D] mb-3">Despre eveniment</div>
              <div className={`text-[14px] text-[#F8F8F4]/90 leading-relaxed whitespace-pre-line ${!expanded && "line-clamp-6"}`}>
                {ev.description_long}
              </div>
              {ev.description_long.length > 400 && (
                <button type="button" onClick={() => setExpanded(!expanded)} className="mt-3 text-[11px] font-bold tracking-widest text-[#E89BB8] hover:text-[#F2BBD0] inline-flex items-center gap-1">
                  {expanded ? "MAI PUȚIN" : "CITEȘTE MAI MULT"} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
                </button>
              )}
            </div>
          )}

          {ev.agenda && ev.agenda.length > 0 && (
            <div className="dense-card rounded-lg p-5" data-testid="event-agenda">
              <div className="text-[10px] uppercase tracking-widest text-[#D4A63D] mb-4">Program / Agendă</div>
              <ol className="space-y-3">
                {ev.agenda.map((item, i) => (
                  <li key={i} className="flex gap-4 items-start">
                    <div className="shrink-0 min-w-[70px] font-display text-[15px] gold-text pt-0.5 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" /> {item.time}
                    </div>
                    <div className="flex-1 pb-3 border-b border-white/5 last:border-0 last:pb-0">
                      <div className="text-[14px] text-[#F8F8F4]">{item.title}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {ev.tags && ev.tags.length > 0 && (
            <div className="dense-card rounded-lg p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#D4A63D] mb-3">Ce primești</div>
              <div className="flex flex-wrap gap-2">
                {ev.tags.map((t) => (
                  <span key={t} className="text-[11.5px] px-2.5 py-1 rounded-sm bg-[#D4A63D]/10 text-[#D4A63D] border border-[#D4A63D]/25 uppercase tracking-widest">{t}</span>
                ))}
              </div>
            </div>
          )}

          <ShareButtons url={typeof window !== "undefined" ? window.location.href : ""} title={ev.title} />
        </div>

        {/* RIGHT: Booking Form */}
        <aside id="rezerva" className="lg:col-span-1 lg:sticky lg:top-24 self-start">
          <div className="dense-card rounded-lg p-5" data-testid="booking-form-card">
            <div className="text-[10px] uppercase tracking-widest text-[#E89BB8] mb-1">Rezervă locul tău</div>
            <h3 className="font-display text-xl text-[#F8F8F4] leading-tight">Adaugă-te la această întâlnire</h3>
            {!canBook ? (
              <div className="mt-4 p-3 bg-[#C83A3A]/10 border border-[#C83A3A]/25 rounded-sm text-[12px] text-[#F8F8F4]">
                {ev.status === "sold_out" ? "Ne pare rău, toate locurile sunt ocupate." : "Acest eveniment s-a încheiat."}
                <Link to="/intalniri" className="mt-2 block text-[#D4A63D] font-bold">Vezi alte evenimente →</Link>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-4 space-y-2.5">
                <div className="grid grid-cols-2 gap-2">
                  <input required data-testid="book-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Prenume" className="bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#D4A63D] outline-none" />
                  <input required data-testid="book-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Nume" className="bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#D4A63D] outline-none" />
                </div>
                <input required type="email" data-testid="book-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#D4A63D] outline-none" />
                <input type="tel" data-testid="book-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefon (opțional)" className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#D4A63D] outline-none" />
                <div className="grid grid-cols-2 gap-2">
                  <input data-testid="book-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Oraș" className="bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#D4A63D] outline-none" />
                  <input data-testid="book-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Țară" className="bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#D4A63D] outline-none" />
                </div>

                <select data-testid="book-participant-type" value={form.participant_type} onChange={(e) => setForm({ ...form, participant_type: e.target.value })} className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#D4A63D] outline-none">
                  {PARTICIPANT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>

                <textarea data-testid="book-story" rows={3} value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} placeholder="Povestea ta pe scurt (opțional)" className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#D4A63D] outline-none resize-none" />
                <textarea data-testid="book-special-needs" rows={2} value={form.special_needs} onChange={(e) => setForm({ ...form, special_needs: e.target.value })} placeholder="Nevoi speciale (alergii, mobilitate, etc.)" className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#D4A63D] outline-none resize-none" />

                {ev.price > 0 && (
                  <div className="pt-2">
                    <div className="text-[10px] uppercase tracking-widest text-[#A0AABF] mb-1.5">Cum plătești?</div>
                    <div className="space-y-1.5" data-testid="book-payment-options">
                      {PAYMENT_OPTIONS.map((po) => {
                        const Icon = po.icon;
                        const active = form.payment_option === po.v;
                        // deposit only if deposit_amount available or price > 100
                        if (po.v === "deposit" && !(ev.deposit_amount || ev.price > 100)) return null;
                        return (
                          <button
                            type="button"
                            key={po.v}
                            onClick={() => setForm({ ...form, payment_option: po.v })}
                            data-testid={`book-pay-${po.v}`}
                            className={`w-full text-left px-3 py-2 rounded-sm border text-[12px] transition-colors flex items-center gap-2 ${active ? "border-[#D4A63D] bg-[#D4A63D]/10 text-[#F8F8F4]" : "border-white/10 text-[#A0AABF] hover:border-white/20"}`}
                          >
                            <Icon className={`w-3.5 h-3.5 ${active ? "text-[#D4A63D]" : ""}`} />
                            {po.l}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <label className="flex items-start gap-2 text-[11px] text-[#A0AABF] pt-2 cursor-pointer">
                  <input type="checkbox" data-testid="book-public" checked={form.public} onChange={(e) => setForm({ ...form, public: e.target.checked })} className="mt-0.5" />
                  <span>Sunt de acord să apar public în lista de participanți (opțional).</span>
                </label>
                <label className="flex items-start gap-2 text-[11px] text-[#A0AABF] cursor-pointer">
                  <input required type="checkbox" data-testid="book-terms" checked={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.checked })} className="mt-0.5" />
                  <span>Accept <Link to="/termeni" className="text-[#D4A63D] underline">termenii și condițiile</Link> și <Link to="/confidentialitate" className="text-[#D4A63D] underline">politica de confidențialitate</Link>.</span>
                </label>

                <button type="submit" disabled={submitting} data-testid="book-submit" className="w-full btn-gold rounded-sm py-3 text-[11px] font-bold tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50 mt-3">
                  <Send className="w-4 h-4" /> {submitting ? "SE PROCESEAZĂ..." : (ev.price > 0 ? `REZERVĂ · ${form.payment_option === "deposit" ? Math.round((ev.deposit_amount || ev.price * 0.2)) : ev.price.toFixed(0)} RON` : "REZERVĂ GRATUIT")}
                </button>

                <p className="text-[10px] text-[#6C7A9C] text-center mt-2">
                  🔒 Plată securizată prin Stripe. Vei primi confirmare pe email.
                </p>
              </form>
            )}
          </div>

          <a
            href={waLink(`Salut, am o întrebare despre evenimentul „${ev.title}”.`)}
            target="_blank" rel="noreferrer"
            data-testid="event-whatsapp"
            className="mt-3 dense-card rounded-lg p-3.5 flex items-center gap-3 group hover:border-[#25D366]/40 transition-colors"
          >
            <MessageCircle className="w-5 h-5 text-[#25D366]" />
            <div className="flex-1">
              <div className="text-[12.5px] text-[#F8F8F4] font-medium group-hover:text-[#25D366]">Ai întrebări? Scrie pe WhatsApp</div>
              <div className="text-[10px] text-[#6C7A9C] uppercase tracking-widest mt-0.5">Răspundem în &lt; 24h</div>
            </div>
          </a>
        </aside>
      </section>
    </div>
  );
}
